import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Filter, SessionCookieService, TableService } from '@docpath/linces-angular-components';
import { BreadcrumbItem, CardInfo, DpHeaderOption, Table } from '@docpath/webcomponents';
import { faSignal } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { Constants } from 'src/app/constants/constants';
import { RoutingPath } from 'src/app/constants/routing-path';
import { DgeState } from 'src/app/pages/configuration/engines/models/dge';
import { SinclairCard, SinclairCardFilterOption, SinclairCardFilterOptionType } from 'src/app/shared/components/sinclair-cards/model/sinclair-card-type';
import { CloudService } from 'src/app/shared/services/cloud.service';
import { environment } from 'src/environments/environment';

import { AppSession } from '../../shared/models/app-session.model';
import { StatisticsTableService } from './components/table/statistics-table.service';
import { Job } from './models/job';
import { StatisticsService } from './services/statistics.service';
import { StatisticsHttpService } from './services/statistics-http.service';
@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html'
})
export class StatisticsComponent implements OnInit, OnDestroy {

  private readonly i18nPrefix = 'statistics';
  
  breadCrumb: BreadcrumbItem[] = [];

  faSignal = faSignal;
  title = 'statistics.title';

  public CARD_FILTERS_DGES = 'dges';
  public CARD_FILTERS_NUMBER_OF_HOURS = 'numberHours';

  sinclairCards: SinclairCard[] = [];

  dgesFilter: SinclairCardFilterOption[] = [];
  numberOfHoursFilter: SinclairCardFilterOption[] = [];

  cardsInfo: CardInfo[] = [];

  private table?: Table;
  private tableChangedSubscription?: Subscription;
  selectedRow: Job[] = [];
  filters: Filter[] = [];
  private readonly tableName = 'statistics';

  dgeId: number = 0;
  numberHours: number = Constants.NUMBER_OF_HOURS_24;
  customNumberHoursFrom: string = '';
  customNumberHoursTo: string = '';

  idRefreshProcess = 0;

  // eslint-disable-next-line max-params
  constructor(
    private readonly statisticsService: StatisticsService,
    private readonly statisticsHttpService: StatisticsHttpService,
    private readonly statisticsTableService: StatisticsTableService,
    private readonly tableService: TableService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly sessionCookieService: SessionCookieService,
    private readonly translateService: TranslateService,
    private readonly cloudService: CloudService
  ) {
    const columns = this.tableService.getColumns(this.tableName),
      defaultColumns = this.statisticsTableService.getDefaultColumns();

    defaultColumns.forEach(defaultColumn => {
      const column = columns.find(column => column.name === defaultColumn.name);
      if (column) {
        defaultColumn.visible = column.visible;
      }
    });

    this.tableService.updateColumns(this.tableName, defaultColumns);

  }

  onTableLoaded(table: Table): void {
    this.table = table;
  }

  ngOnInit(): void {

    this.translateService.onLangChange.subscribe(() => {
      this.reloadBreadCrumb();
    });

    this.reloadBreadCrumb();

    this.dgeId = Number(this.activatedRoute.snapshot.paramMap.get('id'));
    this.dgeId = Number.isNaN(this.dgeId) ? 0 : this.dgeId;

    this.loadNumberOfHoursFiltersOptions();
    this.loadDgesFiltersOptions().subscribe(() => {
      this.loadCards();

      this.clearUrl();
      if (this.dgeId > 0) {
        this.updateDgeFilterActiveByDgeIdUrl();
      }
    });

    this.loadCardsInfo();

    this.statisticsService.statisticsSelectsChanged.subscribe(statisticsFilter => {
      this.dgeId = statisticsFilter.dgeId;
      this.numberHours = statisticsFilter.numberHours;
    });

    if (!this.tableChangedSubscription) {
      this.tableChangedSubscription = this.statisticsService.tableChanged.subscribe(() => {
        this.statisticsTableService.loadJobs(true, this.dgeId ?? Constants.DGE_ALL_VALUES, this.numberHours, this.customNumberHoursFrom, this.customNumberHoursTo, 0, this.filters, 1);
      });
    }

    const appSession = this.sessionCookieService.getSessionCookie()?.app as AppSession;
    this.initRefreshProcess(appSession.refreshLastHour * 1000);

  }
  
    private reloadBreadCrumb() {
      this.breadCrumb = [
        { label: environment.APP_NAME, width: '240px' },
        { label: this.translateService.instant(`${this.i18nPrefix}.title`) }
      ];
    }

  private initRefreshProcess(refreshLastHour: number) {

    this.idRefreshProcess = setInterval(() => {
      // Refresh process only if the number of hours is different to 0 (Custom search)
      // and if all home elements to refresh are ready
      if (this.numberHours !== Constants.NUMBER_OF_HOURS_0
        && this.statisticsService.isStatisticsElementsReadyToRefresh()) {
        // reset statistics elements ready to refresh
        this.statisticsService.resetStatisticsElementsReadyToRefresh();

        // Refresh cards info
        this.loadCardsInfoValue(this.customNumberHoursFrom, this.customNumberHoursTo, 0);

        // Refresh Charts
        this.statisticsService.notifyStatisticsRefreshChange(this.dgeId, this.numberHours);
      }
    }, refreshLastHour) as unknown as number;

  }

  ngOnDestroy() {

    if (this.idRefreshProcess) {
      clearInterval(this.idRefreshProcess);
    }

  }

  loadCards(): void {

    this.sinclairCards = [
      new SinclairCard({
        name: this.CARD_FILTERS_DGES, rows: 8, title: this.dgesFilter.find(f => f.active)?.title as string,
        subTitle: this.dgesFilter.find(f => f.active)?.subTitle as string,
        tooltip: this.dgesFilter.find(f => f.active)?.tooltip as string,
        filters: this.dgesFilter, filtersHeader: `${this.i18nPrefix}.cards.filters.dges.header`,
        filtersOptionTooltip: `${this.i18nPrefix}.cards.filters.dges.optionsTooltip`
      }),
      new SinclairCard({
        name: this.CARD_FILTERS_NUMBER_OF_HOURS, rows: 4, title: this.numberOfHoursFilter.find(f => f.active)?.title as string,
        tooltip: this.numberOfHoursFilter.find(f => f.active)?.tooltip as string, filters: this.numberOfHoursFilter,
        filtersOptionTooltip: `${this.i18nPrefix}.cards.filters.number-hours.optionsTooltip`, customStyles: { cssClass: 'text-dpmedium' }
      })
    ];

  }

  loadDgesFiltersOptions(): Observable<void> {

    return new Observable(observer => {
      this.statisticsHttpService.getAllDge().subscribe(result => {
        this.dgesFilter.push(new SinclairCardFilterOption({
          type: SinclairCardFilterOptionType.DgeFilters,
          title: `${this.i18nPrefix}.cards.filters.dges.default.label`,
          subTitle: `${this.i18nPrefix}.cards.filters.dges.default.labelSuffix`,
          value: Constants.DGE_ALL_VALUES,
          tooltip: `${this.i18nPrefix}.cards.filters.dges.default.tooltip`,
          active: true
        }));

        result.forEach(dge => {
          this.dgesFilter.push(new SinclairCardFilterOption({
            type: SinclairCardFilterOptionType.DgeFilters,
            title: dge.name,
            subTitle: dge.dgeDescription,
            value: dge.id,
            tooltip: dge.name,
            labelState: dge.currentState,
            stateClass: { cssClass: this.getStateClassForDgeState(dge.currentState) }
          }));
        });

        observer.next();
        observer.complete();
      });
    });

  }

  private getStateClassForDgeState(dgeState: string): string {

    switch (dgeState) {
      case DgeState.RUNNING: return 'dpgreen';
      case DgeState.STOPPING: return 'dpyellow';
      default: return 'dpred';
    }

  }

  loadNumberOfHoursFiltersOptions() {

    this.numberOfHoursFilter = [
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.last-24-hours.label`,
        value: Constants.NUMBER_OF_HOURS_24, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.last-24-hours.tooltip`, active: true
      }),
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.last-12-hours.label`,
        value: Constants.NUMBER_OF_HOURS_12, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.last-12-hours.tooltip`
      }),
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.last-6-hours.label`,
        value: Constants.NUMBER_OF_HOURS_6, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.last-6-hours.tooltip`
      }),
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.last-hour.label`,
        value: Constants.NUMBER_OF_HOURS_1, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.last-hour.tooltip`
      }),
      new SinclairCardFilterOption({
        type: SinclairCardFilterOptionType.Label, title: `${this.i18nPrefix}.cards.filters.number-hours.custom.label`,
        value: Constants.NUMBER_OF_HOURS_0, tooltip: `${this.i18nPrefix}.cards.filters.number-hours.custom.tooltip`
      })
    ];

  }

  loadCardsInfo(): void {

    this.cardsInfo = [
      new CardInfo({
        customStylesRow: { cssClass: 'col-3 ps-0' },
        text: this.cloudService.isModeCloud() ? `${this.i18nPrefix}.cards.info.generated-transactions.label` : `${this.i18nPrefix}.cards.info.generated-documents.label`,
        textTooltip: this.cloudService.isModeCloud() ? `${this.i18nPrefix}.cards.info.generated-transactions.tooltip` : `${this.i18nPrefix}.cards.info.generated-documents.tooltip`,
        value: '',
        valueTooltip: '0'
      }),
      new CardInfo({
        customStylesRow: { cssClass: 'col-3' }, text: `${this.i18nPrefix}.cards.info.generated-pages.label`,
        textTooltip: `${this.i18nPrefix}.cards.info.generated-pages.tooltip`, value: '', valueTooltip: '0'
      }),
      new CardInfo({
        customStylesRow: { cssClass: 'col-3' }, text: `${this.i18nPrefix}.cards.info.executed-jobs.label`,
        textTooltip: `${this.i18nPrefix}.cards.info.executed-jobs.tooltip`, value: '', valueTooltip: '0'
      }),
      new CardInfo({
        customStylesRow: { cssClass: 'col-3 pe-0' }, text: `${this.i18nPrefix}.cards.info.errors.label`,
        textTooltip: `${this.i18nPrefix}.cards.info.errors.tooltip`, value: '', valueTooltip: '0', customStylesValue: { cssClass: 'text-dpred' }
      })
    ];

    this.loadCardsInfoValue(this.customNumberHoursFrom, this.customNumberHoursTo, 0);

  }

  private loadCardsInfoValue(customNumberHoursFrom: string, customNumberHoursTo: string, lastJobId: number) {

    // Show spinner without value (only first time)
    if (this.cardsInfo[0].value === '') {
      this.cardsInfo[0].loading = true;
      this.cardsInfo[1].loading = true;
      this.cardsInfo[2].loading = true;
      this.cardsInfo[3].loading = true;
    }

    this.statisticsHttpService.getCardsInfo({ dgeId: this.dgeId, numberHours: this.numberHours, customNumberHoursFrom, customNumberHoursTo, lastJobId }).subscribe(result => {
      this.cardsInfo[0].value = this.cloudService.isModeCloud() ? result.generatedTransactions.toString() : result.generatedDocuments.toString();
      this.cardsInfo[0].valueTooltip = this.cloudService.isModeCloud() ? result.generatedTransactions.toString() : result.generatedDocuments.toString();
      this.cardsInfo[0].loading = false;
      this.cardsInfo[1].value = result.generatedPages.toString();
      this.cardsInfo[1].valueTooltip = result.generatedPages.toString();
      this.cardsInfo[1].loading = false;
      this.cardsInfo[2].value = result.executedJobs.toString();
      this.cardsInfo[2].valueTooltip = result.executedJobs.toString();
      this.cardsInfo[2].loading = false;
      this.cardsInfo[3].value = result.errors.toString();
      this.cardsInfo[3].valueTooltip = result.errors.toString();
      this.cardsInfo[3].loading = false;
    });

  }

  loadAllComponents(): void {

    const dge: number = this.sinclairCards.find(c => c.name === this.CARD_FILTERS_DGES)?.filters.find(f => f.active)?.value as number;
    const numberHours: number = this.sinclairCards.find(c => c.name === this.CARD_FILTERS_NUMBER_OF_HOURS)?.filters.find(f => f.active)?.value as number;

    if (numberHours === Constants.NUMBER_OF_HOURS_0) {
      const customDateRange = this.sinclairCards.find(c => c.name === this.CARD_FILTERS_NUMBER_OF_HOURS)?.title.split(' - ');
    
      this.customNumberHoursFrom = customDateRange![0];
      this.customNumberHoursTo = customDateRange![1];

      this.statisticsHttpService.getLastJobId(dge).subscribe(result => {
        this.loadAllComponentsValues(dge, numberHours, result);
      });
    } else {
      this.customNumberHoursFrom = '';
      this.customNumberHoursTo = '';

      this.loadAllComponentsValues(dge, numberHours, 0);
    }

  }

  private loadAllComponentsValues(dge: number, numberHours: number, lastJobId: number): void {
    this.statisticsService.notifyStatisticsSelectsChange(dge, numberHours, this.customNumberHoursFrom, this.customNumberHoursTo, lastJobId);

    this.loadCardsInfoValue(this.customNumberHoursFrom, this.customNumberHoursTo, lastJobId);
  }

  cardFiltersClicked(card: SinclairCard): void {

    if (card.name === this.CARD_FILTERS_DGES) {
      const dgeIdActive = card.filters.find(f => f.active)?.value as number;
      this.dgesFilter = [];
      this.loadDgesFiltersOptions().subscribe(() => {
        card.filters = this.dgesFilter;

        // Set active false to all filters and active true to the filter with the dgeIdActive
        card.filters.find(f => f.value === Constants.DGE_ALL_VALUES)!.active = false;
        const filter = card.filters.find(f => f.value === dgeIdActive);
        if (filter) {
          filter.active = true;
        }
      });
    }

  }

  OnDestroy(): void {
    if (this.tableChangedSubscription) {
      this.tableChangedSubscription.unsubscribe();
    }
  }

  onCheckChange(): void {
    if (this.table) {
      this.selectedRow = this.statisticsTableService.getSelectedJob(this.table);
    }
  }

  getTable(): Table | undefined {
    return this.table;
  }

  onFilterChange(filters: Filter[]): void {
    this.filters = filters;
  }

  getSelectedJobCount(): number {
    if (this.table) {
      const jobs = this.statisticsTableService.getSelectedJob(this.table);
      return jobs.length;
    }
    return 0;
  }

  getSelectedJob(): Job | undefined {
    if (this.table) {
      const jobs = this.statisticsTableService.getSelectedJob(this.table);
      return jobs.length === 1 ? jobs[0] : undefined;
    }

    return undefined;
  }

  onColumnVisibilityChange(option: DpHeaderOption): void {
    const columnsConfig = this.tableService.getColumns(this.tableName),
      currentColumn = columnsConfig.find(column => column.name === option.value);

    if (currentColumn) {
      currentColumn.visible = option.checked;
      this.tableService.updateColumn(this.tableName, currentColumn);
    }

    if (this.table) {
      this.statisticsTableService.refreshColumns(this.table);
    }
  }

  private clearUrl(): void {

    const context = window.location.pathname.split('/')[1];
    const path = RoutingPath.PATH_STATISTICS;
    const url = `/${context}/${path}`;
    history.pushState({}, '', url);

  }

  private updateDgeFilterActiveByDgeIdUrl() {

    const card = this.sinclairCards.find(c => c.name === this.CARD_FILTERS_DGES);

    if (card) {
      card.filters.forEach(filter => {
        if (filter.value === this.dgeId) {
          filter.active = true;
        } else {
          filter.active = false;
        }
      });
    }

  }

}
